import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import Loading from "../../../components/Loading";
import { useRequest } from "../../../hooks/UseRequest";
import { setNotification } from "../../../redux/notificationSlice";
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { selectDropdownOptions, selectDropdowns } from '../../../redux/dropdownSlice'
import { selectAuth } from '../../../redux/authSlice'
import SpecialInstruction from '../../../components/elements/SpecialInstruction'

const ExpressLoanModal = () => {
  const requests = useRequest()
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth)
  const modal = useSelector(selectModal);
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const customerId = modal.options?.customerId
  const [saving, setSaving] = useState(false)
  const { handleSubmit, control, reset, formState: { errors }, } = useForm();
  const request = useRequest()
  const navi = useNavigate()
  const [expressLoanCreated, setExpressLoanCreated] = useState(false)

  const payFrequency = dropdowns.data.filter((o) => o.code_key === 'pay_frequency')[0]
  const payFrequencyOptions = dropdownOptions.filter((o) => o.dropdown_id === payFrequency.id && o.code_key !== 'pay_frequency_daily').map((o) => ({ label: o.name, value: o.id }))

  const dayOfWeek = dropdowns.data.filter((o) => o.code_key === 'day_of_week')[0]
  const dayOfWeekOptions = dropdownOptions.filter((o) => o.dropdown_id === dayOfWeek.id).map((o) => ({ label: o.name, value: o.id }))

  const weekOfMonth = dropdowns.data.filter((o) => o.code_key === 'week_of_month')[0]
  const weekOfMonthOptions = dropdownOptions.filter((o) => o.dropdown_id === weekOfMonth.id).map((o) => ({ label: o.name, value: o.id }))

  const dayOfMonth = dropdowns.data.filter((o) => o.code_key === 'day_of_month')[0]
  const dayOfMonthOptions = dropdownOptions.filter((o) => o.dropdown_id === dayOfMonth.id).map((o) => ({ label: o.name, value: o.id }))

  const [payFrequencyValue, setPayFrequencyValue] = useState("")
  const [payFrequencyValueOption, setPayFrequencyValueOption] = useState("")

  const [customer, setCustomer] = useState({})
  const [customerBankAccounts, setCustomerBankAccounts] = useState([])
  const inFlightCustomerId = useRef(null)
  const active = modal.is_active && modal.type === 'express_loan'

  const resetState = () => {
    reset()
    setExpressLoanCreated(false)
    setSaving(false)
    setCustomer({})
    setCustomerBankAccounts([])
    setPayFrequencyValue("")
    setPayFrequencyValueOption("")
  }

  /**
   * Get current data for the customer for Agent to confirm before expressing new loan.
   * This modal stays mounted in the Footer, so every opening starts from a clean slate — whatever
   * path closed it last time — before loading this customer's data.
   */
  useEffect(() => {
    if (active && customerId) {
      resetState()
      getExpressLoanInfo(customerId)
    } else {
      inFlightCustomerId.current = null
    }
  }, [active, customerId])

  const getExpressLoanInfo = async (forCustomerId) => {
    inFlightCustomerId.current = forCustomerId
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${forCustomerId}/express_loan_info`)
      const response = await requests.get()
      // Ignore a response that a close or a newer opening has superseded.
      if (inFlightCustomerId.current !== forCustomerId) return

      const bankAccountOptions = response?.data?.data?.customer_bank_accounts.map((e, i) => {
        return { label: `Routing #: ${e.bank_routing_number}, Account #: ${e.bank_account_number_last_four}`, value: e.id }
      })

      setCustomer(response?.data?.data?.customer)
      setCustomerBankAccounts(bankAccountOptions)
    } catch (error) {
      if (inFlightCustomerId.current !== forCustomerId) return
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const onSubmit = async (formData) => {
    setExpressLoanCreated(false)
    if (!formData.loan_amount) {
      dispatch(setNotification({ type: 'error', body: "Loan Amount is required" }))
      return
    }

    if (!formData.customer_bank_account_id.value) {
      dispatch(setNotification({ type: 'error', body: "Customer Bank Account is required" }))
      return
    }

    if (!formData.pay_frequency_dropdown_option_id.value) {
      dispatch(setNotification({ type: 'error', body: "Pay Frequency is required" }))
      return
    }

    let data = {
      pay_first_day_of_week_dropdown_option_id: null,
      next_pay_date: null,
      pay_first_day_of_month_dropdown_option_id: null,
      pay_second_day_of_month_dropdown_option_id: null,
      pay_first_week_of_month_dropdown_option_id: null,
      pay_second_week_of_month_dropdown_option_id: null,
      pay_second_day_of_week_dropdown_option_id: null,
    }

    if (formData.pay_frequency_dropdown_option_id.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_weekly')?.id) {
      if (!formData.pay_first_day_of_week_dropdown_option_id.value) {
        dispatch(setNotification({ type: 'error', body: "Pay day of week is required" }))
        return;
      } else {
        data.pay_first_day_of_week_dropdown_option_id = formData.pay_first_day_of_week_dropdown_option_id.value
      }
    }

    if (formData.pay_frequency_dropdown_option_id.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_biweekly')?.id) {
      if (!formData.next_pay_date) {
        dispatch(setNotification({ type: 'error', body: "Next Pay Date is required" }))
        return;
      } else {
        data.next_pay_date = formData.next_pay_date
      }
    }

    if (formData.pay_frequency_dropdown_option_id.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_semimonthly')?.id) {
      if (payFrequencyValueOption === "semimonthly-option1") {
        if (!formData.pay_first_day_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "First payment day of month is required" }))
          return;
        } else if (!formData.pay_second_day_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Second payment day of month is required" }))
          return;
        } else {
          data.pay_first_day_of_month_dropdown_option_id = formData.pay_first_day_of_month_dropdown_option_id.value
          data.pay_second_day_of_month_dropdown_option_id = formData.pay_second_day_of_month_dropdown_option_id.value
        }
      } else if (payFrequencyValueOption === "semimonthly-option2") {
        if (!formData.pay_first_week_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Week 1 is required" }))
          return;
        } else if (!formData.pay_first_day_of_week_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Day 1 is required" }))
          return;
        } else if (!formData.pay_second_week_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Week 2 is required" }))
          return;
        } else if (!formData.pay_second_day_of_week_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Day 2 is required" }))
          return;
        } else {
          data.pay_first_week_of_month_dropdown_option_id = formData.pay_first_week_of_month_dropdown_option_id.value
          data.pay_first_day_of_week_dropdown_option_id = formData.pay_first_day_of_week_dropdown_option_id.value
          data.pay_second_week_of_month_dropdown_option_id = formData.pay_second_week_of_month_dropdown_option_id.value
          data.pay_second_day_of_week_dropdown_option_id = formData.pay_second_day_of_week_dropdown_option_id.value
        }
      } else {
        dispatch(setNotification({ type: 'error', body: "Please select a semi monthly option" }));
        return;
      }
    }

    if (formData.pay_frequency_dropdown_option_id.value === dropdownOptions.find((option) => option?.code_key === 'pay_frequency_monthly')?.id) {
      if (payFrequencyValueOption === "monthly-option1") {
        if (!formData.pay_first_day_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Day is required" }))
          return;
        } else {
          data.pay_first_day_of_month_dropdown_option_id = formData.pay_first_day_of_month_dropdown_option_id.value
        }
      } else if (payFrequencyValueOption === "monthly-option2") {
        if (!formData.pay_first_week_of_month_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Week is required" }))
          return;
        } else if (!formData.pay_first_day_of_week_dropdown_option_id.value) {
          dispatch(setNotification({ type: 'error', body: "Day is required" }))
          return;
        } else {
          data.pay_first_week_of_month_dropdown_option_id = formData.pay_first_week_of_month_dropdown_option_id.value
          data.pay_first_day_of_week_dropdown_option_id = formData.pay_first_day_of_week_dropdown_option_id.value
        }
      } else {
        dispatch(setNotification({ type: 'error', body: "Please select a monthly option" }));
        return;
      }
    }

    // Set the saving state to true, indicating the operation is in progress sd
    setSaving(true);

    // Configure the API endpoint
    request.setEndpoint(`${process.env.REACT_APP_LEAD_FUNNEL_DOMAIN}/express`)

    // Initialize the request payload
    let body = {
      loan_amount: formData.loan_amount,
      customer_id: customerId,
      customer_bank_account_id: formData.customer_bank_account_id.value,
      pay_frequency_dropdown_option_id: formData.pay_frequency_dropdown_option_id.value,
      ...data // Payroll Data
    }

    try {
      const response = await request.post(body)
      // If the operation was successful, execute the success callback and reset the form
      if (response.status === 200 || response.status === 201) {
        setExpressLoanCreated(true)
        setSaving(false)
      } else {
        // If the operation was not successful, show an error notification
        setExpressLoanCreated(false)
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      // If an exception occurs, reset the saving state and show either the
      // denial popup (for DS denials) or a generic error notification.
      setExpressLoanCreated(false)
      setSaving(false)
      if (error?.response?.data?.error_key === 'lead_funnel_denial') {
        handleCancel()
        dispatch(setModal({ is_active: true, type: 'ds_denied_modal', options: { source: 'express_loan' } }))
        return
      }
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.error_key
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  };

  if (!active) {
    return null;
  }

  const handleCancel = () => {
    resetState()
    dispatch(setModal({ is_active: false }));
  };

  const handleUpdateBankAccountClick = () => {
    handleCancel()
    dispatch(
      setModal({
        is_active: true,
        type: 'bank_account_modal',
        options: { customerId: customerId }
      })
    )
  }

  const goToManualVerificationPage = () => {
    handleCancel()
    navi(`/manual_verification/${customerId}`)
  }

  const payFrequencyFormFields = () => {
    return (
      <>
        <div className="field is-horizontal mt-3">
          <label className="express-loan-modal-label">
            Pay Frequency:
            <span
              className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
              data-tooltip={`How often do you get paid?`}
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
            </span>
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="pay_frequency_dropdown_option_id"
              control={control}
              defaultValue={''}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_frequency_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={payFrequencyOptions}
                  isSearchable={false}
                  onChange={(val) => {
                    field.onChange({ label: val?.label, value: val?.value })
                    setPayFrequencyValue(val?.label)
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if semi-monthly option is selected */}
        <div className={`field is-horizontal mt-3 ${payFrequencyValue === "Semi-Monthly" ? "" : "is-hidden"}`}>
          <label className="express-loan-modal-label">
            Semi Monthly Option:
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="semi_monthly_pay_frequency"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="semi_monthly_pay_frequency"
                  classNames={reactSelectClassNames()}
                  options={[{
                    label: 'Specific Days',
                    value: 'semimonthly-option1'
                  }, { label: 'Week Day and Week of Month', value: 'semimonthly-option2' }]}
                  isSearchable={false}
                  onChange={(val) => {
                    field.onChange({ label: val?.label, value: val?.value })
                    setPayFrequencyValueOption(val?.value)
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if monthly option is selected */}
        <div className={`field is-horizontal mt-3 ${payFrequencyValue === "Monthly" ? "" : "is-hidden"}`}>
          <label className="express-loan-modal-label">
            Monthly Option:
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="monthly_pay_frequency"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="monthly_pay_frequency"
                  classNames={reactSelectClassNames()}
                  options={[{
                    label: 'Pay Day of Months',
                    value: 'monthly-option1'
                  }, { label: 'Week Day and Week of Month', value: 'monthly-option2' }]}
                  isSearchable={false}
                  onChange={(val) => {
                    field.onChange({ label: val?.label, value: val?.value })
                    setPayFrequencyValueOption(val?.value)
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Week Day and Week of Month choosen */}
        {/* display this option if Monthly and Week Day and Week of Month choosen */}
        <div className={`field is-horizontal mt-3 ${((payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") || (payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2")) ? "" : "is-hidden"}`}>
          <label className="express-loan-modal-label">
            {payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2" && (<>Week 1:</>)}
            {payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2" && (<>Week:</>)}
            <span
              className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
              data-tooltip={`
                    ${(payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") ? "What days of each week do you get paid?" : ""}
                    ${(payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2") ? "What week of month and day of that week do you get paid?" : ""}
                  `}
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
            </span>
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="pay_first_week_of_month_dropdown_option_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_first_week_of_month_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={weekOfMonthOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if weekly choosen */}
        {/* display this option if semi-monthly and week day adn week month choosen */}
        {/* display this option if Monthly and Week Day and Week of Month choosen */}
        <div className={`field is-horizontal mt-3 ${(payFrequencyValue === "Weekly" || (payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") || (payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2")) ? "" : "is-hidden"}`}>
          <label className="express-loan-modal-label">
            {payFrequencyValue === "Weekly" && (<>Pay day of week:</>)}
            {payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2" && (<>Day 1:</>)}
            {payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2" && (<>Day:</>)}
            <span
              className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
              data-tooltip={`
                        ${payFrequencyValue === "Weekly" ? "What day of the week do you get paid on?" : ""}
                        ${(payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") ? "What days of each week do you get paid?" : ""}
                        ${(payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2") ? "What week of month and day of that week do you get paid?" : ""}
                  `}
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
            </span>
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="pay_first_day_of_week_dropdown_option_id"
              control={control}
              defaultValue={''}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_first_day_of_week_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={dayOfWeekOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Week Day and Week of Month choosen */}
        <div className={`field is-horizontal mt-3 ${(payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") ? "" : "is-hidden"}`}>
          <label className="express-loan-modal-label">
            Week 2:
            <span
              className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
              data-tooltip={`What days of each week do you get paid?`}
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
            </span>
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="pay_second_week_of_month_dropdown_option_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_second_week_of_month_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={weekOfMonthOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Week Day and Week of Month choosen */}
        <div className={`field is-horizontal mt-3 ${(payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") ? "" : "is-hidden"}`}>
          <label className="express-loan-modal-label">
            Day 2:
            <span
              className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
              data-tooltip={`What days of each week do you get paid?`}
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
            </span>
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="pay_second_day_of_week_dropdown_option_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_second_day_of_week_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={dayOfWeekOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if bi-weekly choosen */}
        <div className={`field is-horizontal mt-3 ${payFrequencyValue === "Bi-Weekly" ? "" : "is-hidden"}`}>
          <label className="express-loan-modal-label">
            Next Pay Date:
            <span
              className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
              data-tooltip={`When is your next pay date?`}
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
            </span>
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="next_pay_date"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  className="input is-small"
                  type="date"
                  onChange={(val) => field.onChange(val)}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Specific Days choosen */}
        {/* display this option if Monthly and Pay Day of Months choosen */}
        <div className={`field is-horizontal mt-3 ${((payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option1") || (payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option1")) ? "" : "is-hidden"}`}>
          <label className="express-loan-modal-label">
            {payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option1" && (<>First payment day of month:</>)}
            {payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option1" && (<>Day:</>)}
            <span
              className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
              data-tooltip={`
                    ${(payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option1") ? "What two days do you get paid each month?" : ""}
                    ${(payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option1") ? "What day of the month do you get paid?" : ""}
                  `}
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
            </span>
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="pay_first_day_of_month_dropdown_option_id"
              control={control}
              defaultValue={""}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_first_day_of_month_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={dayOfMonthOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Specific Days choosen */}
        <div className={`field is-horizontal mt-3 ${(payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option1") ? "" : "is-hidden"}`}>
          <label className="express-loan-modal-label">
            Second payment day of month:
            <span
              className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
              data-tooltip={`What two days do you get paid each month?`}
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
            </span>
          </label>
          <div className="control express-loan-modal-field">
            <Controller
              name="pay_second_day_of_month_dropdown_option_id"
              control={control}
              defaultValue={''}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_second_day_of_month_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={dayOfMonthOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                />
              )}
            />
          </div>
        </div>
      </>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="express_loan_modal">
      <div className="modal is-active is-large">
        <div className="modal-background"></div>
        <div className="modal-card modal-card-express-loan column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Express Loan
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: "110px" }}
          >
            {Object.keys(customer).length === 0 && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  <span className="has-text-center"><Loading /></span>
                </div>
              </div>
            )}
            {Object.keys(customer).length > 0 && (
              <>
                <div className="columns mb-0 is-flex-direction-column">
                  <div className="column">
                    <h3>
                      Use this action when, a customer at one point had a new lead, but cannot proceed in the application
                      process with that lead. Expressing a new application will draw on the existing customer information
                      we have to create a new application to complete. If there is a still a valid existing lead, use it instead.
                    </h3>
                  </div>
                </div>

                <div className="field is-horizontal mt-3">
                  <label className={`express-loan-modal-label`}>Loan Amount:</label>
                  <div className="control express-loan-modal-field">
                    <Controller
                      name="loan_amount"
                      control={control}
                      defaultValue={''}
                      render={({ field }) => (
                        <input {...field} type="number" id="loan_amount" className="input is-small" />
                      )}
                    />
                    <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                      {errors.loan_amount &&
                        <span>{errors.loan_amount.message ? errors.loan_amount.message : "Loan Amount is required"}</span>}
                    </p>
                  </div>
                </div>

                <div className="field is-horizontal mt-3">
                  <label className="express-loan-modal-label">
                    Customer Bank Account
                    <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                      data-tooltip={``}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </label>
                  <div className="control express-loan-modal-field">
                    <Controller
                      name="customer_bank_account_id"
                      control={control}
                      defaultValue={''}
                      render={({ field }) => (
                        <Select
                          {...field}
                          name="customer_bank_account_id"
                          classNames={reactSelectClassNames()}
                          options={customerBankAccounts}
                          isSearchable={false}
                          onChange={(val) => {
                            field.onChange({ label: val?.label, value: val?.value })
                          }}
                        />
                      )}
                    />
                    <a onClick={() => handleUpdateBankAccountClick()} className="help">
                      Can&apos;t find the bank account? Click here to add a new Bank Account <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </a>
                  </div>
                </div>

                {payFrequencyFormFields()}

                <SpecialInstruction
                  content={"<span>" +
                    "<span class='has-text-weight-bold'>This will perform the following actions:</span>" + "<br>" +
                    "<span class='pl-2'>• Check Customer Details Against Exclusion List (OEL)</span> <br> " +
                    "<span class='pl-2'>• Run a check for MLA and OFAC</span> <br> " +
                    "<span class='pl-2'>• Create a new Lead for this customer</span> <br> " +
                    "<span class='pl-2'>• Validate and update loan terms based on new information</span> <br> " +
                    "<span class='pl-2'>• Copy over existing verification data if completed in the last 14 days</span> <br> " +
                    " </span>"}
                  type="is-warning"
                />
                <div className="field is-horizontal mt-3">
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.express_process &&
                      <span>{errors.express_process.message ? errors.express_process.message : "Loan Express has error!"}</span>}
                  </p>
                </div>
              </>
            )}

          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white"
                onClick={handleCancel}
              >
                Cancel
              </button>
              {!expressLoanCreated && (
                <button type="submit" className="button is-link is-small" disabled={saving && 'disabled'}>
                  {saving ? (
                    <>
                      Submitting
                      <Loading size="is-size-6" />
                    </>
                  ) : 'Express Loan'}
                </button>
              )}
              {expressLoanCreated && (
                <button type="button" className="button is-link is-small" onClick={() => goToManualVerificationPage()}>Go to Verification</button>
              )}
            </div>
          </footer>
        </div>
      </div>
    </form>
  );
};

export default ExpressLoanModal;
